import { fillMissing } from "object-fill-missing-keys";
import { initializeGeoJson } from 'store/functions/geoJsonFunctions.js';
import { canonicalTablesToInternal } from 'store/functions/scenarioConversion.js';
import { reconcileTravelCostTyping } from 'store/functions/travelCostTyping.js';
import { addRowHelper } from 'store/reducers/helpers';

import {
  DEFAULT_LOGIN_DATA,
  DEFAULT_NOTIFICATIONS,
  DEFAULT_OPTIMIZATION,
  DEFAULT_TABLE_CONFIGS,
  INITIAL_DATA,
  INITIAL_GENERAL_SETTINGS,
  INITIAL_GEO_JSON_FILTERS,
  NON_TABLE_DATA_KEYS,
  STATIC_MODEL_SETTINGS,
} from 'store/reducers/constants/constants.js';


/** The short-lived model type that the coordinate-system setting replaced. */
const LEGACY_XY_MODEL_TYPE = 'xy-routing';

/**
 * Do this scenario's locations live on a plane rather than on the globe?
 *
 * The API's contract is that a location carries `latitude`/`longitude` or
 * `x`/`y`, never both, so the data answers this on its own. Used only when the
 * scenario does not say — an explicit setting is always believed.
 *
 * Any real latitude/longitude anywhere makes it geographic: a scenario that
 * mixes the two is malformed, and guessing "cartesian" would throw away
 * coordinates we can actually place on a map.
 */
function locationsAreCartesian(locations) {
  if (!Array.isArray(locations) || locations.length === 0) return false;

  let sawPlaneCoordinates = false;
  for (const location of locations) {
    if (Number.isFinite(location?.latitude) && Number.isFinite(location?.longitude)) {
      return false;
    }
    // 0 is a legitimate coordinate here — a depot at the origin is the norm on
    // a board or a warehouse floor — so this is a finiteness test, not truthiness.
    if (Number.isFinite(location?.x) && Number.isFinite(location?.y)) {
      sawPlaneCoordinates = true;
    }
  }
  return sawPlaneCoordinates;
}

/**
 * Settle the coordinate system for a scenario being loaded.
 *
 * `fillMissing` back-fills `coordinateSystem: 'geographic'` whenever the
 * scenario omits it, which is right for everything saved before the setting
 * existed — and wrong for anything whose coordinates are plane values. Two
 * kinds of scenario land in that hole:
 *
 *   - legacy `xy-routing` saves, from before the coordinate system became a
 *     per-scenario setting rather than a model type;
 *   - published cartesian examples, which arrive with `mapping: {mapMode}` and
 *     no coordinate system at all. Loading one showed the geographic map and
 *     flagged every row for a missing latitude and longitude it was never
 *     supposed to have.
 *
 * `statedCoordinateSystem` must come from the RAW input, before `fillMissing`
 * runs — afterwards every scenario claims to have stated one.
 */
function resolveCoordinateSystem(generalSettings, { statedCoordinateSystem, isLegacyXyModel, locations }) {
  if (!generalSettings) return generalSettings;
  // An explicit statement is always believed, including an explicit 'geographic'.
  if (statedCoordinateSystem) return generalSettings;
  if (!isLegacyXyModel && !locationsAreCartesian(locations)) return generalSettings;

  return {
    ...generalSettings,
    mapping: { ...generalSettings.mapping, coordinateSystem: 'cartesian' },
  };
}

// A stored geoJson is only usable if it actually holds features. An empty
// object ({}) is truthy in JS, so we must check contents — otherwise a
// scenario saved without geoJson (e.g. a freshly authored example) would render
// a blank map instead of rebuilding its geometry from the data tables.
const hasGeoJsonContent = (geoJson) =>
  Boolean(geoJson) && Array.isArray(geoJson.features) && geoJson.features.length > 0;

const getDefaultTableSettings = (inputTableConfigs) => {
  let defaultSettings = {};
  for (const [tableName, tableSettings] of Object.entries(inputTableConfigs)) {
    const tableDefaultSettings = tableSettings;
    defaultSettings[tableName] = tableDefaultSettings;
  }
  return defaultSettings
}

const getDefaultGeneralSettings = () => {
  let generalSettings = {...INITIAL_GENERAL_SETTINGS};
  generalSettings['scenario'] = JSON.parse(JSON.stringify(INITIAL_GENERAL_SETTINGS['scenario'])) || {'scenarioName': 'New Scenario', 'lastSaved': null}
  return generalSettings
}

const initializeDataHelper = (state, data) => {
  state['data'] = fillMissing(JSON.parse(JSON.stringify(data)), INITIAL_DATA);

  /*
  A scenario that brought its own travel costs must not have any invented for it.

  Re-adding a location generates a cost row per pair, and re-adding a vehicle
  type now generates one per pair too. That only ever worked here by accident of
  key order: `travel_costs` happened to come after `locations`, so the generated
  rows were thrown away when the table was rebuilt. Order it the other way — a
  saved scenario is JSON, and nothing guarantees its key order — and they would
  be appended to the real rows instead, duplicating every pair, which the solver
  cannot read either. Saying so explicitly also saves building and discarding
  n^2 rows on every example load.

  When the scenario supplies no travel costs, the rows generated from its
  locations ARE the table, so that one case keeps the old behaviour.
  */
  const suppliedTravelCosts = Array.isArray(data['travel_costs']) && data['travel_costs'].length > 0;

  Object.keys(data).forEach((tableName) => {
    if (tableName === 'travel_costs' && data['travel_costs'].length === 0) {
      // Skip initialization for empty travel_costs table
    } else {
      state['data'][tableName] = []
      const tableData = data[tableName];
      // Non-table keys (resultSummary, resultBaseline, resultAmendments) hold
      // objects/logs, not rows — running them through addRowHelper would crash.
      // They are re-attached explicitly on scenario load.
      if (!NON_TABLE_DATA_KEYS.includes(tableName)) {
      tableData.forEach((row) => {
        let newRow = {...row}
        state = addRowHelper({
          state: state, newRow: newRow, uuid: newRow['UUID'], tableName: tableName,
          validateCorrespondingColumns: false,
          addCostRow: tableName === 'locations' && !suppliedTravelCosts,
        });
      })
    }}
  })

  return state
}

const removeExtraKeys = (inputDict, referenceDict) => {
  /**
   * This function removes keys from inputDict that are not in referenceDict
   * @param {*} inputData: the input data to be cleaned
   * @returns: the cleaned data
   */

  const helperFunc = (inputDict, referenceDict) => {
    let newDict = {};
    for (const [key, val] of Object.entries(inputDict)) {
      if (referenceDict[key] === undefined) {
        continue;
      }
      if (typeof val === 'object' && !Array.isArray(val)) {
        newDict[key] = helperFunc(val, referenceDict[key]);
      } else {
        newDict[key] = inputDict[key];
      }
    }
    return newDict;
  }

  const returnDict =  helperFunc(inputDict, referenceDict);
  return returnDict;
}

const resultsAvailable = (state) => {
  const activeResultsTable = STATIC_MODEL_SETTINGS[state.activeModelType]['resultsTable'];
  return state.data[activeResultsTable] && state.data[activeResultsTable].length > 0;
}

export const initializeData = (inputData) => {
  /*
  Function to initialize inputData (both the data dict and the tableSettings dict)

  Args:
    inputData: JSON object containing the state data
  */
  // Normalize incoming data tables (canonical OpenAPI shape -> internal rows:
  // UUID keys + resolved display columns). Idempotent on already-internal data.
  const internalData = (inputData && inputData.data)
    ? canonicalTablesToInternal(inputData.data)
    : INITIAL_DATA;

  // Only trust a persisted geoJson when the scenario actually has results.
  // Otherwise rebuild from the input tables: geoJson is derived state, and a
  // saved scenario can carry stale result geometry (routeLeg/routePoint) even
  // after its results table was cleared — which would render routes on the map
  // that the UI (keyed on the results table) considers absent.
  // 'xy-routing' was briefly a model type before the coordinate system became
  // a per-scenario setting. Scenarios saved then must load as vehicle-routing
  // *on the x-y plot* — without this they'd fall through to the default below
  // and silently reinterpret plane coordinates as latitude/longitude.
  const savedModelType = inputData && inputData.activeModelType;
  const isLegacyXyModel = savedModelType === LEGACY_XY_MODEL_TYPE;
  const activeModelType = (isLegacyXyModel ? 'vehicle-routing' : savedModelType) || 'vehicle-routing';
  const resultsTable = STATIC_MODEL_SETTINGS[activeModelType]
    && STATIC_MODEL_SETTINGS[activeModelType]['resultsTable'];
  const hasResults = resultsTable
    && Array.isArray(internalData[resultsTable])
    && internalData[resultsTable].length > 0;

  let newState = {
    // A copy, not the shared constant: the travel-cost reconciliation below can
    // put a notice in here, and pushing into DEFAULT_NOTIFICATIONS would leave
    // it in every scenario loaded afterwards for the life of the tab.
    notifications: JSON.parse(JSON.stringify(DEFAULT_NOTIFICATIONS)),
    activeMode: 'modeling',
    activeModelType: activeModelType,
    tableSettings: JSON.parse(JSON.stringify(getDefaultTableSettings((inputData && fillMissing(inputData.tableSettings, DEFAULT_TABLE_CONFIGS)) || DEFAULT_TABLE_CONFIGS))),
    generalSettings: resolveCoordinateSystem(
      (inputData && fillMissing(inputData.generalSettings, INITIAL_GENERAL_SETTINGS)) || getDefaultGeneralSettings(),
      {
        // Read before fillMissing, which would otherwise make every scenario
        // look as though it had stated one.
        statedCoordinateSystem: inputData?.generalSettings?.mapping?.coordinateSystem,
        isLegacyXyModel,
        // The RAW rows: canonicalTablesToInternal moves x/y into
        // latitude/longitude, so after conversion nothing looks cartesian.
        locations: inputData?.data?.locations,
      },
    ),
    // Filled in below, once the tables have been initialized — see the note there.
    geoJson: { type: 'FeatureCollection', features: [] },
    optimization: DEFAULT_OPTIMIZATION,
    geoJsonFilter: (inputData && inputData.geoJsonFilter && fillMissing(inputData.geoJsonFilter, INITIAL_GEO_JSON_FILTERS)) || INITIAL_GEO_JSON_FILTERS,
    loginData: (inputData && inputData.loginData) || DEFAULT_LOGIN_DATA,
    loading: {}
    };

    newState = initializeDataHelper(newState, internalData)
    newState['data'] = removeExtraKeys(newState['data'], INITIAL_DATA);

    /*
    Travel costs and vehicle types, reconciled before anything can act on them.

    Loading is where every stale shape arrives: scenarios saved before rows
    carried a vehicleTypeId (2026-09-15), persisted localStorage from the same
    era, and the published examples, whose matrices are fully typed by id while
    their vehicle-type column is switched off. Rows keep the authority — the
    display flag is set from what they carry, so nothing downstream can decide
    the matrix is untyped because a column is hidden. Relinked rows raise one
    notice; back-filling a display name from an id is silent, since the solver
    never reads the name.
    */
    reconcileTravelCostTyping(newState, { notify: true });

    // Build the geoJson from the *initialized* tables rather than the raw input, so
    // the map and the grid agree on every column. Previously this ran against the
    // raw data — before initializeDataHelper stamped the schema's defaultValues on
    // each row — so any column the scenario omitted reached the map as undefined and
    // fell back to whatever default the map layer itself chose. Location colour was
    // the visible symptom: examples carry no colour, so the grid showed the schema
    // default while the map drew its own, and the two only converged once the user
    // edited the row.
    newState.geoJson = (inputData && hasGeoJsonContent(inputData.geoJson) && hasResults)
      ? inputData.geoJson
      : initializeGeoJson(newState['data']);

    if (!resultsAvailable(newState)) {
      newState['geoJsonFilter']['locationLayerVisible'] = true;
    }

    if (STATIC_MODEL_SETTINGS[newState['activeModelType']].useLocationDepot) {
      newState.tableSettings['locations']['isViewable'] = {...newState.tableSettings['locations']['isViewable'], 'Depot': true}
    } else {
      newState.tableSettings['locations']['isViewable'] = {...newState.tableSettings['locations']['isViewable'], 'Depot': false}
    }

  return newState;
}
